/**
 * @module config/featureFlags
 * @description Lightweight feature flags to decouple *deploying* code from
 * *releasing* it. A flag defaulting off lets you ship a feature dark; flipping it
 * on makes it visible without another build.
 *
 * Resolution order (later overrides earlier):
 *   1. DEFAULTS below
 *   2. build-time env: VITE_FEATURE_<NAME>=true|false
 *   3. the server's switches (Settings → Modules, appadmin; GET /api/auth/features)
 *   4. runtime global: window.__FELICE_FLAGS__ = { name: true } (set by an
 *      optional, non-bundled /runtime-config.js you can edit on the server)
 *   5. localStorage "featureFlags" (per-browser manual/dev override)
 *
 * NOTE: the *per-user* go-live lever for an app feature is still the `tasks` app
 * permission (granting it needs no redeploy). This flag is the global on/off
 * (e.g. hide a half-baked feature from everyone, including appadmins).
 */

import { reactive } from "vue";
import { authApi } from "@/utils/api.js";

const DEFAULTS = {
  // Hidden 2026-10-09 (Maurizio: no longer needed; to be REMOVED once he has
  // decided — YEAR_END_PLAN.md, Phase 5). Hidden for everybody, appadmins too:
  // sidebar, Home cards, Settings entries and direct URLs (router guard).
  // To bring one back: set it true here and redeploy, or build with
  // VITE_FEATURE_<NAME>=true, or (no rebuild) window.__FELICE_FLAGS__ via the
  // server's runtime-config.js, or this browser only:
  //   localStorage.setItem("featureFlags", JSON.stringify({ reviews: true }))
  marketing: false, // the Marketing landing page and its sidebar group
  campaigns: false, // campaign tracking
  reviews: false, // reviews, review assignments, review admin, account managers
  disputes: false,
  tasks: false, // (was released 2026-06-20)
};

function envOverrides() {
  const out = {};
  for (const [key, val] of Object.entries(import.meta.env || {})) {
    const m = /^VITE_FEATURE_(.+)$/.exec(key);
    if (m) out[m[1].toLowerCase()] = String(val).toLowerCase() === "true";
  }
  return out;
}

function localStorageFlags() {
  try {
    return JSON.parse(localStorage.getItem("featureFlags") || "{}");
  } catch {
    return {};
  }
}

const windowFlags = (typeof window !== "undefined" && window.__FELICE_FLAGS__) || {};

// What an appadmin switched in Settings → Modules (filled by ensureFeatureFlags()).
let serverFlags = {};

const compute = () => ({
  ...DEFAULTS,
  ...envOverrides(),
  ...serverFlags,
  ...windowFlags,
  ...localStorageFlags(),
});

// Reactive, so the sidebar and Home update the moment a switch is saved or the
// server's answer arrives.
export const featureFlags = reactive(compute());

/** True only when the named feature is explicitly enabled. */
export function isFeatureEnabled(name) {
  return featureFlags[name] === true;
}

/** The modules Settings → Modules lists (keep in step with backend/auth/utils/featureFlags.js). */
export const MODULE_SWITCHES = [
  { key: "marketing", title: "Marketing", description: "The Marketing landing page and its sidebar group" },
  { key: "campaigns", title: "Campaigns", description: "Campaign tracking and the campaign calendars" },
  { key: "reviews", title: "Reviews", description: "Reviews, review assignments, review admin and account managers" },
  { key: "disputes", title: "Disputes", description: "Platform disputes" },
  { key: "tasks", title: "Tasks", description: "Tasks and task settings" },
];

/** Apply the server's switches over the code defaults. */
export function applyServerFlags(flags) {
  serverFlags = flags && typeof flags === "object" ? flags : {};
  Object.assign(featureFlags, compute());
}

let loading = null;
/**
 * Load the server's switches once per page load. Never throws: if the server
 * does not answer (or is an older build without the endpoint) the code defaults
 * stay — a module is then hidden, never shown by accident.
 */
export function ensureFeatureFlags() {
  if (!loading) {
    loading = authApi
      .get("/api/auth/features")
      .then(({ data }) => applyServerFlags(data?.flags))
      .catch(() => {});
  }
  return loading;
}

/** Save switches (appadmin only) and apply the stored result at once. */
export async function saveFeatureFlags(flags) {
  const { data } = await authApi.put("/api/admin/features", { flags });
  applyServerFlags(data.flags);
  return data.flags;
}
